import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import MomentUtils from '@date-io/moment';
import { SnackbarProvider } from 'notistack';
import {
  createStyles,
  jssPreset,
  makeStyles,
  StylesProvider,
  ThemeProvider
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import CryptoJS from 'crypto-js';
import Auth from 'src/components/Auth';
import GoogleAnalytics from 'src/components/GoogleAnalytics';
import ScrollReset from 'src/components/ScrollReset';
import useSettings from 'src/hooks/useSettings';
import { createTheme } from 'src/theme';
import Routes from 'src/Routes';
import configSingletonInstance from './ConfigSingleton';
import ConfigContext from './config/ConfigProvider';
import ProductType from './components/ProductType';
import getProductType from './components/ProductType';
import { routesConfig } from './fetchRouteJSON';
import LoadingScreen from './components/LoadingScreen';

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const useStyles = makeStyles(() =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%'
      },
      body: {
        height: '100%',
        width: '100%'
      },
      '#root': {
        height: '100%',
        width: '100%'
      },
      '@global': {
        '*::-webkit-scrollbar': {
          '-webkit-appearance': 'none',
          height: '0.4em',
          width: '0.4em'
        },

        '*::-webkit-scrollbar-thumb': {
          'border-radius': '4px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          '-webkit-box-shadow': '0 0 1px rgba(255,255,255,.5)'
        }
      }
    }
  })
);

function App() {
  useStyles();

  const { settings } = useSettings();
  const [config, setConfig] = useState();

  const productType = getProductType();
  const [currentFavicon, setCurrentFavicon] = useState('/favicon.ico');

  const changeFavicon = () => {
    const newFavicon =
      productType === 'Precisa' ? '/favicon.ico' : '/signzy.ico';
    setCurrentFavicon(newFavicon);

    // Update the favicon element
    const link = document.querySelector("link[rel*='icon']");
    link.href = newFavicon;
  };

  // Helper to convert Base64 to string
  const fromBase64 = str => decodeURIComponent(escape(atob(str)));

  // Decrypt Encrypted JSON
  const decryptJSON = (encryptedData, secretKey) => {
    try {
      // Step 1: Decrypt AES-encrypted data
      const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
      const base64Decrypted = bytes.toString(CryptoJS.enc.Utf8);

      if (!base64Decrypted) throw new Error('Invalid key or data');

      // Step 2: Decode Base64 back to JSON
      const jsonString = fromBase64(base64Decrypted);
      const jsonString2 = fromBase64(jsonString);

      return JSON.parse(jsonString2);
    } catch (error) {
      alert(
        'An error occurred while processing your request. Please try again or contact support if the issue persists.'
      );
    }
  };

  const getConfig = () => {
    fetch('/config.json')
      .then(r => r.json())
      .then(
        configs => {
          const config = decryptJSON(
            configs?.configData,
            process.env.REACT_APP_SECURE_KEY_CONFIG_DATA
          );
          localStorage.setItem('apiHost', config.apiHost);
          configSingletonInstance.setConfig(config);
          setConfig(config);
        },
        () => {
          getConfig();
        }
      )
      .catch(() => {
        getConfig();
      });
  };

  useEffect(() => {
    getConfig();
  }, []);

  React.useEffect(() => {
    changeFavicon();
  }, [productType]);

  if (!config) {
    return <LoadingScreen />;
  }

  return (
    <ConfigContext.Provider value={config}>
      <ThemeProvider theme={createTheme(settings)}>
        <StylesProvider jss={jss}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <SnackbarProvider maxSnack={1}>
              <Router history={history}>
                <Auth>
                  <ScrollReset />
                  <GoogleAnalytics />
                  <Routes router={routesConfig(getProductType())} />
                </Auth>
              </Router>
            </SnackbarProvider>
          </MuiPickersUtilsProvider>
        </StylesProvider>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
}

export default App;
